<script setup lang="ts">
import {
  apps,
  formatting,
  LegacyDelivery,
  LegacyOrder,
  legacyOrderCommands,
  legacyOrderKey,
  useEnums,
  useLocalization
} from '@hantera/portal-app'
import {ContextMenuButton, DataColumnValuesEnumValue, DropdownItem, EnumsBar, Panel} from '@hantera/design-system'
import {computed, inject, ref} from 'vue';
import OrderRow from '@/components/LegacyOrder/OrderRow';
import AppSlotView from '@/AppSlotView.vue';
import Decimal from 'decimal.js';
import useBackend from '@/composition/useBackend';

const props = defineProps<{
  data: LegacyDelivery
}>();

const order = inject(legacyOrderKey, ref<LegacyOrder>(<any>{}));

const { currency, date } = formatting;
const { legacyOrders: orders, legacyTagDefinitions: tagDefinitions } = useBackend();
const t = useLocalization();
const enums = useEnums()

const inventoryDate = computed(() => {
  return date(props.data.inventoryDateUtc);
});

const deliveryTotal = computed(() => {
  return currency(
    props.data.rows.reduce((sum, row) => sum.add(row.rowTotal), new Decimal(0))
      .add(props.data.deliveryTotal),
    order.value.currencyCode
  );
})

function addTag(value: DataColumnValuesEnumValue) {
  orders.buildCommandGroup(
    order.value.orderId,
    'Add tag {tagName} to delivery {deliveryNumber}', {
    tagNumber: new Decimal(value.key),
    tagName: value.label,
    deliveryNumber: props.data.deliveryNumber,
  })
    .command(legacyOrderCommands.addDeliveryTag, {
      deliveryId: props.data.deliveryId,
      tagNumber: new Decimal(value.key)
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Added tag {tagName} to delivery {deliveryNumber}',
      fields: {
        tagName: value.label,
        deliveryNumber: props.data.deliveryNumber
      }
    })
    .send();
}

function removeTag(value: DataColumnValuesEnumValue) {
  const tag = props.data.tags.find(r => r.tagNumber.eq(value.key))!
  
  orders.buildCommandGroup(
    order.value.orderId,
    'Removing tag {tagName} from delivery {deliveryNumber}', {
    tagNumber: tag.tagNumber,
    tagName: value.label,
    deliveryNumber: props.data.deliveryNumber,
  })
    .command(legacyOrderCommands.removeDeliveryTag, {
      deliveryId: props.data.deliveryId,
      tagNumber: tag.tagNumber,
      actionId: tag.actionId && tag.actionId !== '00000000-0000-0000-0000-000000000000' ? tag.actionId : undefined
    })
    .command(legacyOrderCommands.addActivityLog, {
      message: 'Removed tag {tagName} from delivery {deliveryNumber}',
      fields: {
        tagName: value.label,
        deliveryNumber: props.data.deliveryNumber
      }
    })
    .send();
}

const canBeSetToProcessing = computed(() => props.data.state === 'Open');
const canBeCancelled = computed(() => props.data.state === 'Open');
const canBeSetToReleased = computed(() => props.data.state === 'Picking');

function setToProcessing() {
  orders.buildCommandGroup(
    order.value.orderId,
    "Set delivery {deliveryNumber} to {state}",
    {
      deliveryNumber: props.data.deliveryNumber,
      state: 'Processing'
    }
  )
    .command(legacyOrderCommands.setDeliveryAsProcessing, {
      deliveryId: props.data.deliveryId
    })
    .send();
}
function setToReleased() {
  orders.buildCommandGroup(
    order.value.orderId,
    "Set delivery {deliveryNumber} to {state}",
    {
      deliveryNumber: props.data.deliveryNumber,
      state: 'Released'
    }
  )
    .command(legacyOrderCommands.setDeliveryAsReleased, {
      deliveryId: props.data.deliveryId
    })
    .send();
}
function cancelDelivery() {
  orders.buildCommandGroup(
    order.value.orderId,
    "Cancel delivery {deliveryNumber}",
    {
      deliveryNumber: props.data.deliveryNumber,
    }
  )
    .command(legacyOrderCommands.cancelDelivery, {
      deliveryId: props.data.deliveryId
    })
    .send();
}

const availableTags = computed(() => {

  const legacyTagKeys = new Set<string>(tagDefinitions.value.deliveryTags.map(r => r.number.toString()))

  return enums.valueSets('delivery', 'tags').value
    .map(set => {

      return {
        ...set,
        values: set.values
          .filter(v => legacyTagKeys.has(v.key))
      }
    })
})

const selectedTags = computed(() => {
  return props.data.tags.map(r => r.tagNumber.toString())
})

</script>

<template>
  <Panel class="legacy-delivery">

    <div class="legacy-delivery__header">
      <div
        class="legacy-delivery__state"
        :style="`background-color: var(--state-colors__delivery-${data.state.toLowerCase()})`"
        :title="data.state"
      ></div>
      <div class="legacy-delivery__header-details">
        <div class="legacy-delivery__title">
          <div
            class="legacy-delivery__number"
            data-sentry-unmask
          >{{ data.deliveryNumber ?? t('orderView.delivery.newDelivery') }}</div>
          <div
            class="legacy-delivery__inventory"
            data-sentry-unmask
          >{{ data.inventoryName }}</div>
          <div
            class="legacy-delivery__inventory-date"
            data-sentry-unmask
          >{{ inventoryDate }}</div>
          <AppSlotView
            :slot="apps.legacySlots.legacyOrder.delivery.header"
            :context="{
              delivery: data
            }"
          />
        </div>
        <EnumsBar
          :available-sets="availableTags"
          :selected-values="selectedTags"
          @add="addTag"
          @remove="removeTag"
        >
<!--          <template #dropdown>-->
<!--            <DropdownItem @click="() => enums.openEditor('delivery', 'tags')">-->
<!--              Configure Tags-->
<!--            </DropdownItem>-->
<!--          </template>-->
        </EnumsBar>
      </div>
      <ContextMenuButton
        class="legacy-delivery__context-menu"
        data-sentry-unmask
      >
        <DropdownItem
          v-if="canBeSetToProcessing"
          @click="setToProcessing"
        >{{ t('orderView.delivery.setToProcessing') }}</DropdownItem>
        <DropdownItem
          v-if="canBeSetToReleased"
          @click="setToReleased"
        >{{ t('orderView.delivery.setToReleased') }}</DropdownItem>
        <DropdownItem
          v-if="canBeCancelled"
          @click="cancelDelivery"
        >{{ t('orderView.delivery.cancel') }}</DropdownItem>
        <AppSlotView
          :slot="apps.legacySlots.legacyOrder.delivery.contextMenu"
          :context="{
            delivery: data
          }"
        />
      </ContextMenuButton>
    </div>

    <div class="legacy-order-rows">
      <OrderRow
        v-for="row in data.rows"
        :key="row.rowId"
        :delivery="data"
        :row="row"
      />
  
      <AppSlotView
        :slot="apps.legacySlots.legacyOrder.delivery.rows"
        :context="{
          delivery: data
        }"
      />
    </div>

    <div class="legacy-delivery__total">
      {{ deliveryTotal }}
    </div>

    <div class="legacy-delivery__footer">
      <AppSlotView
        :slot="apps.legacySlots.legacyOrder.delivery.footer"
        :context="{
          delivery: data
        }"
      />
    </div>
  </Panel>
</template>

<style>
.legacy-delivery {
  max-width: 800px;
  position: relative;
}

.legacy-delivery__header {
  position: relative;
  padding-left: 10px;
  margin-bottom: 5px;
  display: flex;
  align-items: center;
}

.legacy-delivery__state {
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 5px;
  border-radius: 5px;
}

.legacy-delivery__header-details {
  flex-grow: 1;
}

.legacy-delivery__title {
  display: flex;
  justify-content: flex-start;
  flex-wrap: wrap;
  column-gap: 10px;
  row-gap: 3px;
  align-items: center;
}

.legacy-delivery__number {
  font-size: 16px;
  font-weight: 600;
  line-height: 22px;
  white-space: nowrap;
}

.legacy-delivery__inventory {
  white-space: nowrap;
}

.legacy-delivery__inventory-date {
  white-space: nowrap;
}

.legacy-delivery__context-menu {
  height: 32px;
  width: 32px;
}

.legacy-delivery__tags {
  display: flex;
}

.legacy-delivery__summary-row {
  padding: 5px;
}

.legacy-delivery__total {
  text-align: right;
  font-weight: 600;
  font-size: 16px;
}

.legacy-delivery__footer {
  margin: 0 -5px -5px -5px;
}

.legacy-order-rows {
  display: grid;
  grid-template-columns: auto [middle] repeat(99, min-content);
  grid-auto-rows: min-content;
  grid-auto-columns: min-content;
  row-gap: 5px;
}

@container (max-width: 700px) {
  .legacy-order-rows {
    grid-template-columns: auto repeat(99, min-content);
  }
}
</style>